import type { FC } from 'react'
import styled from '@emotion/styled'

import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronLeftIcon } from '@segment/matcha/components/Icons/Common'

import Image from '@segment/matcha/components/Image'

import HeroBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

interface Image {
  src?: string
  width?: number
  height?: number
  alt?: string
}

export interface HeroProps {
  episodeNumber?: number
  episodeTitle?: string
  episodeDescription?: string
  episodeImage?: Image
}

const backButton = {
  href: `/good-data-better-marketing/`,
  text: `Back to episodes`,
}

const Hero: FC<HeroProps> = ({ episodeNumber = 0, episodeTitle, episodeDescription, episodeImage }) => {
  const pageConfig = usePageConfig()

  const episode = `Episode ${episodeNumber < 10 ? `0${episodeNumber}` : episodeNumber}`

  return (
    <Wrapper>
      <StyledHeroBase variant="2Col">
        <HeroRecipe.Copy>
          <CopyTextBase>
            <StyledButtonText tag="a" href={getAbsoluteUrl(backButton.href, pageConfig)} iconStart={ChevronLeftIcon}>
              {backButton.text}
            </StyledButtonText>
            <StyledOverline tag="h5" variant="overline">
              {episode}
            </StyledOverline>
            <CopyText.Heading tag="h1" variant="h1">
              {episodeTitle}
            </CopyText.Heading>
            <CopyText.Description tag="p" variant="pExtraLarge">
              {episodeDescription}
            </CopyText.Description>
          </CopyTextBase>
        </HeroRecipe.Copy>
        <StyledHeroRecipeContents>
          {episodeImage && (
            <StyledImage
              alt={String(episodeImage?.alt)}
              src={String(episodeImage?.src)}
              width={episodeImage?.width}
              height={episodeImage?.height}
            />
          )}
        </StyledHeroRecipeContents>
      </StyledHeroBase>
    </Wrapper>
  )
}

const Wrapper = styled.header`
  background: ${({ theme }) => theme.palette.twilio.gray[10]};
  padding-top: 40px;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding-top: 80px;
  }
`

const StyledHeroBase = styled(HeroBase)`
  ${HeroRecipe.Wrapper} {
    padding-top: 0;
    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      grid-template-columns: 56% 34.5%;
    }
  }
`

const StyledButtonText = styled(ButtonText)`
  margin-bottom: 30px;
`

const StyledHeroRecipeContents = styled(HeroRecipe.Contents)`
  min-width: auto;
`

const StyledImage = styled(Image)`
  max-width: 200px;
  margin: 0;

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    max-width: 260px;
  }
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 100%;
  }
`

const StyledOverline = styled(CopyText.Overline)`
  text-transform: none;
  letter-spacing: 0px;
`

export default Hero
